import { useEffect, useRef } from "react";
import { activityTracker } from "../utils/activityTracker";
import type { ActivityActionKey } from "../utils/activityCatalog.generated";

/** Espera sin tipear antes de contar la búsqueda como "usada". */
export const SEARCH_SETTLE_MS = 1_200;
/** Largo mínimo del texto para considerarlo una búsqueda real. */
export const SEARCH_MIN_CHARS = 2;

/**
 * Cuenta el USO de un buscador para Métricas de Actividad — nunca lo buscado.
 *
 * Emite UN evento `action` por "ráfaga" de búsqueda: cuando el texto lleva `SEARCH_SETTLE_MS`
 * sin cambiar y tiene al menos `SEARCH_MIN_CHARS` caracteres. Si después se borra (o queda
 * corto) y se vuelve a escribir, es otra búsqueda. Solo viaja `actionKey` + cuántos resultados
 * hubo; el texto se mira acá para decidir CUÁNDO emitir y se descarta: no se guarda, no se
 * manda, no se loguea.
 *
 * @param actionKey  una de las 4 búsquedas del catálogo (ej. "search_patients_list")
 * @param query      el texto actual del buscador (solo se usa para el timing)
 * @param resultsCount cantidad de resultados para ESE texto
 */
export function useTrackSearchUsed(actionKey: ActivityActionKey, query: string, resultsCount: number): void {
  const reportedRef = useRef(false);
  const resultsRef = useRef(resultsCount);
  resultsRef.current = resultsCount;
  const active = query.trim().length >= SEARCH_MIN_CHARS;

  useEffect(() => {
    if (!active) {
      reportedRef.current = false; // texto borrado/corto: la próxima ráfaga cuenta de nuevo
      return;
    }
    if (reportedRef.current) return;
    const timer = setTimeout(() => {
      reportedRef.current = true;
      activityTracker.trackAction(actionKey, {
        resultsCount: resultsRef.current,
        hadResults: resultsRef.current > 0,
      });
    }, SEARCH_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [active, query, actionKey]);
}
